import { Ear, Headphones, MessagesSquare, Mic, RotateCcw, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

export type StepKind = 'gist' | 'hear' | 'contrast' | 'say' | 'understand' | 'recall' | 'use';

const KIND: Record<StepKind, { label: string; icon: LucideIcon; why: string }> = {
  gist: { label: 'Get the gist', icon: Headphones, why: 'Hear the whole scene before any words.' },
  hear: { label: 'Hear it', icon: Ear, why: 'Sound first, spelling second.' },
  contrast: { label: 'Hear the difference', icon: Ear, why: 'Same pattern, one small word apart.' },
  say: { label: 'Say it', icon: Mic, why: 'Say it aloud: your mouth needs the practice.' },
  understand: { label: 'Understand it', icon: Ear, why: 'Meaning from sound alone, before any words.' },
  recall: { label: 'Recall it', icon: RotateCcw, why: 'Try before you peek: that is what makes it stick.' },
  use: { label: 'Use it', icon: MessagesSquare, why: 'The whole scene, your lines from memory.' },
};

/**
 * The one header every step opens with: what to do (an icon and a verb), who speaks to whom,
 * and, while the learner is new, why this step helps.
 */
export function StepHead({ kind, role, why = false, children }: { kind: StepKind; role?: ReactNode; why?: boolean; children?: ReactNode }) {
  const { label, icon: Icon, why: reason } = KIND[kind];
  return (
    <div className="step-head">
      <div className="step-head-row">
        <span className="step-kind">
          <Icon size={16} aria-hidden="true" />
          {label}
        </span>
        {role}
        {children}
      </div>
      {why && <p className="step-why">{reason}</p>}
    </div>
  );
}
